import { WSLabelProps } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useClientQuery } from "../../../../query/users/queries";
import {
  avatarBankAccount,
  avatarGiftCard
} from "../../../../shared/constants/avatars";
import { MethodType } from "../ManageMethods/types";
import {
  getAccountLabelProps,
  getCreditCardLabelProps,
  getInternalAccountLabelProps,
  getPaymentCardLabelProps
} from "../ManageMethods/utils";
import {
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesPaymentCards
} from "@query";

const emptyLabel = {
  text: "Historical Payout Method",
  avatar: avatarBankAccount
};

export function useMethodLabelProps(
  id?: string,
  type?: MethodType,
  options?: {
    showBalance?: boolean;
  }
) {
  const history = useHistory();
  const userId = useUserId();

  const queryExternalBankAccount =
    queriesExternalBankAccounts.getExternalBankAccount.useQuery(
      { bankAccountId: id as string },
      {
        enabled: !!id && type === "Account"
      }
    );
  const queryInternalAccount =
    queriesInternalAccounts.getInternalAccount.useQuery(
      { internalAccountId: id as string },
      {
        enabled: !!id && type === "InternalAccount"
      }
    );
  const queryInternalAccountBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: id as string },
      {
        enabled: !!id && type === "InternalAccount" && options?.showBalance
      }
    );
  const queryClient = useClientQuery(userId, {
    enabled: !!id && type === "CreditCard"
  });
  const queryPaymentCard = queriesPaymentCards.getPaymentCard.useQuery(
    { paymentCardId: id as string },
    {
      enabled: !!id && type === "PaymentCard"
    }
  );

  const isLoading = useMemo(() => {
    if (type === "Account") {
      return queryExternalBankAccount.isLoading;
    }
    if (type === "InternalAccount") {
      return queryInternalAccount.isLoading;
    }
    if (type === "CreditCard") {
      return queryClient.isLoading;
    }
    if (type === "PaymentCard") {
      return queryPaymentCard.isLoading;
    }

    return undefined;
  }, [
    type,
    queryExternalBankAccount.isLoading,
    queryInternalAccount.isLoading,
    queryClient.isLoading,
    queryPaymentCard.isLoading
  ]);

  const label = useMemo((): WSLabelProps | undefined => {
    if (type === "GiftCard") {
      return { text: "Gift card", avatar: avatarGiftCard };
    }

    if (type === "Account" && queryExternalBankAccount.data) {
      return getAccountLabelProps(queryExternalBankAccount.data);
    }

    if (type === "InternalAccount" && queryInternalAccount.data) {
      return getInternalAccountLabelProps(
        queryInternalAccount.data,
        history.push,
        options?.showBalance && queryInternalAccountBalance.data
          ? Number(
              queryInternalAccountBalance.data.available ??
                queryInternalAccountBalance.data.balance
            )
          : undefined
      );
    }

    if (type === "CreditCard") {
      const creditCard = queryClient.data?.profile.savedPaymentMethods?.find(
        creditCard => creditCard.paymentMethodId === id
      );

      if (creditCard) {
        return getCreditCardLabelProps(creditCard);
      }
    }

    if (type === "PaymentCard" && queryPaymentCard.data) {
      return getPaymentCardLabelProps(queryPaymentCard.data);
    }

    if (id || type === "Historical") {
      return emptyLabel;
    }
  }, [
    history.push,
    id,
    options?.showBalance,
    queryExternalBankAccount.data,
    queryClient.data?.profile.savedPaymentMethods,
    queryInternalAccount.data,
    queryInternalAccountBalance.data,
    queryPaymentCard.data,
    type
  ]);

  return { isLoading, label };
}
